import React, { useEffect, useState, useContext } from 'react';
import { CCard, CCardHeader, CCardBody, CCol, CRow, CFormInput, CButton } from '@coreui/react';
import GoogleFitCaloriesChart from '../Calories';
import GoogleFitBloodPressureChart from '../BloodPressure';
import GoogleFitWeightChart from '../Weight';
import GoogleFitStepsChart from '../Steps';
import GoogleFitSleepChart from '../Sleep';
import GoogleFitBloodSugarChart from '../BloodSugar';
import NutritionTracker from '../NutritionTracker/NutritionTracker';
import ObservationsTable from './ObservationsTable';
import ObservationTrendsChart from './ObservationTrendsChart';
import './HealthDashboard.scss';
import { AuthUserContext } from '../../session/context';
import caloriesIcon from 'src/assets/icons/calories-icon.png';
import bloodPressureIcon from 'src/assets/icons/blood-pressure-icon.png';
import weightIcon from 'src/assets/icons/weight-icon.png';
import stepsIcon from 'src/assets/icons/steps-icon.png';
import sleepIcon from 'src/assets/icons/sleep-icon.png';
import bloodSugarIcon from 'src/assets/icons/blood-sugar-icon.png';
import { toast } from 'react-toastify';
import config from '../../../utils/EnvironmentSwitcher';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { validateObservation, validateObservationRange, createObservationResource } from '../../../utils/fhirValidation';
import { fetchFhirResourceCount } from '../../../utils/fhirPagination';

// Full observation options with LOINC codes
const observationOptions = [
  { label: 'Calories', unit: 'cal', code: 'cal', loinc: '78950-9', display: 'Calories Intake' },
  { label: 'Weight', unit: 'kg', code: 'kg', loinc: '29463-7', display: 'Body Weight' },
  { label: 'Beers Drank', unit: 'count', code: 'count', loinc: '74013-4', display: 'Alcoholic drinks per day' },
  { label: 'Time on Phone', unit: 'minutes', code: 'min', loinc: null, display: 'Time on Phone' },
  { label: 'Steps Taken', unit: 'steps', code: '{steps}', loinc: '55423-8', display: 'Steps Taken' },
  { label: 'Water Intake', unit: 'liters', code: 'L', loinc: '77299-2', display: 'Water Intake' },
  { label: 'Sleep Hours', unit: 'hours', code: 'h', loinc: '93832-4', display: 'Sleep Duration' },
  { label: 'Heart Rate', unit: 'bpm', code: '/min', loinc: '8867-4', display: 'Heart Rate' },
  { label: 'Blood Pressure Systolic', unit: 'mmHg', code: 'mm[Hg]', loinc: '8480-6', display: 'Systolic Blood Pressure' },
  { label: 'Blood Pressure Diastolic', unit: 'mmHg', code: 'mm[Hg]', loinc: '8462-4', display: 'Diastolic Blood Pressure' },
  { label: 'Body Temperature', unit: '°F', code: '[degF]', loinc: '8310-5', display: 'Body Temperature' },
  { label: 'Oxygen Saturation', unit: '%', code: '%', loinc: '59408-5', display: 'Oxygen Saturation' },
  { label: 'Blood Glucose', unit: 'mg/dL', code: 'mg/dL', loinc: '2339-0', display: 'Blood Glucose' },
  { label: 'Exercise Minutes', unit: 'minutes', code: 'min', loinc: '68516-4', display: 'Exercise Duration' },
  { label: 'Meditation Time', unit: 'minutes', code: 'min', loinc: null, display: 'Meditation Time' },
  { label: 'Screen Time', unit: 'hours', code: 'h', loinc: null, display: 'Screen Time' },
  { label: 'Caffeine Intake', unit: 'mg', code: 'mg', loinc: '91595-9', display: 'Caffeine Intake' },
  { label: 'Sugar Intake', unit: 'g', code: 'g', loinc: null, display: 'Sugar Intake' },
  { label: 'Protein Intake', unit: 'g', code: 'g', loinc: '77489-9', display: 'Protein Intake' },
  { label: 'Carbohydrates Intake', unit: 'g', code: 'g', loinc: null, display: 'Carbohydrate Intake' },
  { label: 'Fat Intake', unit: 'g', code: 'g', loinc: null, display: 'Fat Intake' },
  { label: 'Distance Walked', unit: 'km', code: 'km', loinc: null, display: 'Distance Walked' },
  { label: 'Books Read', unit: 'count', code: 'count', loinc: null, display: 'Books Read' },
  { label: 'Work Hours', unit: 'hours', code: 'h', loinc: null, display: 'Work Hours' },
];

// Demo data — generates 7 days of realistic sample observations
const generateDemoObservations = () => {
  const now = new Date();
  const observations = [];
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const date = new Date(now);
    date.setDate(date.getDate() - dayOffset);
    date.setHours(12, 0, 0, 0);
    observations.push({ option: observationOptions.find(o => o.label === 'Heart Rate'), value: 65 + Math.floor(Math.random() * 20), date: date.toISOString() });
    observations.push({ option: observationOptions.find(o => o.label === 'Body Temperature'), value: (97.8 + Math.random() * 1.5).toFixed(1), date: date.toISOString() });
    observations.push({ option: observationOptions.find(o => o.label === 'Oxygen Saturation'), value: 95 + Math.floor(Math.random() * 5), date: date.toISOString() });
    observations.push({ option: observationOptions.find(o => o.label === 'Blood Glucose'), value: 80 + Math.floor(Math.random() * 40), date: date.toISOString() });
    observations.push({ option: observationOptions.find(o => o.label === 'Steps Taken'), value: 3000 + Math.floor(Math.random() * 9000), date: date.toISOString() });
    observations.push({ option: observationOptions.find(o => o.label === 'Sleep Hours'), value: (5 + Math.random() * 4).toFixed(1), date: date.toISOString() });
    if (dayOffset === 0) observations.push({ option: observationOptions.find(o => o.label === 'Weight'), value: (68 + Math.random() * 4).toFixed(1), date: date.toISOString() });
    observations.push({ option: observationOptions.find(o => o.label === 'Calories'), value: 1500 + Math.floor(Math.random() * 1000), date: date.toISOString() });
    observations.push({ option: observationOptions.find(o => o.label === 'Water Intake'), value: (1.5 + Math.random() * 1.5).toFixed(1), date: date.toISOString() });
    if (dayOffset % 2 === 0) {
      observations.push({ option: observationOptions.find(o => o.label === 'Blood Pressure Systolic'), value: 110 + Math.floor(Math.random() * 20), date: date.toISOString() });
      observations.push({ option: observationOptions.find(o => o.label === 'Blood Pressure Diastolic'), value: 70 + Math.floor(Math.random() * 15), date: date.toISOString() });
    }
  }
  return observations;
};

// Sample expense categories for demo charge data
const expenseCategories = [
  { code: 'GROCERY', display: 'Grocery Shopping', category: 'Food & Dining', amountRange: [45, 180] },
  { code: 'RESTAURANT', display: 'Restaurant Dining', category: 'Food & Dining', amountRange: [15, 85] },
  { code: 'COFFEE', display: 'Coffee Shop', category: 'Food & Dining', amountRange: [4, 12] },
  { code: 'TAKEOUT', display: 'Takeout / Delivery', category: 'Food & Dining', amountRange: [18, 55] },
  { code: 'GAS', display: 'Gas Station', category: 'Transportation', amountRange: [35, 75] },
  { code: 'PARKING', display: 'Parking', category: 'Transportation', amountRange: [5, 25] },
  { code: 'RIDESHARE', display: 'Uber / Lyft', category: 'Transportation', amountRange: [12, 45] },
  { code: 'TRANSIT', display: 'Public Transit', category: 'Transportation', amountRange: [2.50, 15] },
  { code: 'ELECTRIC', display: 'Electric Bill', category: 'Utilities', amountRange: [85, 180] },
  { code: 'WATER', display: 'Water Bill', category: 'Utilities', amountRange: [35, 75] },
  { code: 'INTERNET', display: 'Internet Service', category: 'Utilities', amountRange: [60, 120] },
  { code: 'PHONE', display: 'Phone Bill', category: 'Utilities', amountRange: [45, 95] },
  { code: 'NETFLIX', display: 'Netflix', category: 'Subscriptions', amountRange: [15.99, 22.99] },
  { code: 'SPOTIFY', display: 'Spotify', category: 'Subscriptions', amountRange: [10.99, 16.99] },
  { code: 'GYM', display: 'Gym Membership', category: 'Subscriptions', amountRange: [25, 75] },
  { code: 'AMAZON', display: 'Amazon Prime', category: 'Subscriptions', amountRange: [14.99, 14.99] },
  { code: 'CLOUD', display: 'Cloud Storage', category: 'Subscriptions', amountRange: [2.99, 9.99] },
  { code: 'CLOTHING', display: 'Clothing Store', category: 'Shopping', amountRange: [25, 150] },
  { code: 'ELECTRONICS', display: 'Electronics', category: 'Shopping', amountRange: [20, 300] },
  { code: 'AMAZON_SHOP', display: 'Amazon Purchase', category: 'Shopping', amountRange: [15, 120] },
  { code: 'HOME_GOODS', display: 'Home Goods', category: 'Shopping', amountRange: [20, 100] },
  { code: 'PHARMACY', display: 'Pharmacy', category: 'Health', amountRange: [10, 65] },
  { code: 'HAIRCUT', display: 'Haircut / Salon', category: 'Personal Care', amountRange: [20, 75] },
  { code: 'DOCTOR', display: 'Doctor Copay', category: 'Health', amountRange: [25, 50] },
  { code: 'MOVIES', display: 'Movie Theater', category: 'Entertainment', amountRange: [15, 40] },
  { code: 'GAMING', display: 'Video Games', category: 'Entertainment', amountRange: [10, 70] },
  { code: 'CONCERT', display: 'Concert / Events', category: 'Entertainment', amountRange: [50, 200] },
  { code: 'RENT', display: 'Rent Payment', category: 'Housing', amountRange: [1200, 2500] },
  { code: 'INSURANCE', display: 'Insurance Premium', category: 'Insurance', amountRange: [100, 250] },
  { code: 'PET', display: 'Pet Supplies', category: 'Pets', amountRange: [25, 80] },
  { code: 'ATM', display: 'ATM Withdrawal', category: 'Cash', amountRange: [40, 200] },
  { code: 'GIFT', display: 'Gift Purchase', category: 'Gifts', amountRange: [25, 100] },
  { code: 'CHARITY', display: 'Charitable Donation', category: 'Donations', amountRange: [10, 50] },
];

const getRandomAmount = (range) => {
  const [min, max] = range;
  return parseFloat((min + Math.random() * (max - min)).toFixed(2));
};

const generateDemoCharges = () => {
  const now = new Date();
  const charges = [];
  const addExpense = (dayOffset, code, hour = null) => {
    const date = new Date(now);
    date.setDate(date.getDate() - dayOffset);
    date.setHours(hour || (8 + Math.floor(Math.random() * 12)), Math.floor(Math.random() * 60), 0, 0);
    const expense = expenseCategories.find(e => e.code === code);
    if (expense) charges.push({ expense, amount: getRandomAmount(expense.amountRange), date: date.toISOString() });
  };
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    addExpense(dayOffset, 'COFFEE', 7);
    if (Math.random() > 0.3) {
      const foodOptions = ['GROCERY', 'RESTAURANT', 'TAKEOUT'];
      addExpense(dayOffset, foodOptions[Math.floor(Math.random() * foodOptions.length)]);
    }
  }
  addExpense(0, 'GAS', 8); addExpense(0, 'AMAZON_SHOP', 14); addExpense(0, 'TAKEOUT', 19);
  addExpense(1, 'GROCERY', 11); addExpense(1, 'NETFLIX', 0); addExpense(1, 'SPOTIFY', 0);
  addExpense(2, 'RIDESHARE', 9); addExpense(2, 'MOVIES', 19); addExpense(2, 'RESTAURANT', 21);
  addExpense(3, 'ELECTRIC', 10); addExpense(3, 'INTERNET', 10); addExpense(3, 'PHONE', 10); addExpense(3, 'PHARMACY', 14);
  addExpense(4, 'CLOTHING', 13); addExpense(4, 'HOME_GOODS', 15); addExpense(4, 'PARKING', 12); addExpense(4, 'RESTAURANT', 18);
  addExpense(5, 'HAIRCUT', 11); addExpense(5, 'GYM', 0); addExpense(5, 'PET', 16); addExpense(5, 'ATM', 12);
  addExpense(6, 'TRANSIT', 10); addExpense(6, 'CONCERT', 20); addExpense(6, 'GIFT', 14); addExpense(6, 'CHARITY', 15); addExpense(6, 'GAMING', 16);
  const dayOfMonth = new Date().getDate();
  if (dayOfMonth <= 7) {
    const rentDay = dayOfMonth - 1;
    if (rentDay >= 0 && rentDay < 7) {
      addExpense(rentDay, 'RENT', 9); addExpense(rentDay, 'INSURANCE', 9);
      addExpense(rentDay, 'WATER', 9); addExpense(rentDay, 'AMAZON', 0); addExpense(rentDay, 'CLOUD', 0);
    }
  }
  return charges;
};

const HealthDashboard = () => {
  const { authUser } = useContext(AuthUserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Observation state
  const [observationCount, setObservationCount] = useState(0);
  const [selectedObservation, setSelectedObservation] = useState('');
  const [observationValue, setObservationValue] = useState('');
  const [observationDate, setObservationDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoadingDemoData, setIsLoadingDemoData] = useState(false);
  const [demoDataProgress, setDemoDataProgress] = useState({ current: 0, total: 0 });
  const [isLoadingDemoCharges, setIsLoadingDemoCharges] = useState(false);
  const [demoChargeProgress, setDemoChargeProgress] = useState({ current: 0, total: 0 });
  const [chargeCount, setChargeCount] = useState(0);

  const selectedUnit = observationOptions.find(o => o.label === selectedObservation)?.unit || '';

  const getValidToken = async () => {
    const auth = getAuth();
    if (!auth.currentUser) return null;
    try { return await auth.currentUser.getIdToken(); } catch { return null; }
  };

  const fetchFhirData = async () => {
    if (!authUser?.uid) return;
    try {
      const validToken = await getValidToken();
      if (!validToken) return;
      const baseParams = `identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}&_summary=count`;
      const response = await axios.get(`${config.fhirUrl}/fhir/Observation?${baseParams}`, {
        headers: { 'Content-Type': 'application/fhir+json', Authorization: `Bearer ${validToken}` },
      });
      const bundle = response.data || {};
      setObservationCount(Number.isFinite(bundle.total) ? bundle.total : (Array.isArray(bundle.entry) ? bundle.entry.length : 0));
    } catch (err) {
      console.error('Error fetching observation count:', err);
    }
  };

  const fetchChargeCount = async () => {
    if (!authUser?.uid) return;
    try {
      const validToken = await getValidToken();
      if (!validToken) return;
      const baseParams = `identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`;
      const total = await fetchFhirResourceCount(
        `${config.fhirUrl}/fhir/ChargeItem`,
        baseParams,
        { 'Content-Type': 'application/fhir+json', Authorization: `Bearer ${validToken}` },
        {
          httpClient: (url, opts) => fetch(url, opts).then(async (res) => ({
            ok: res.ok,
            status: res.status,
            json: () => res.json(),
          })),
        }
      );
      setChargeCount(total);
    } catch (err) {
      console.error('Error fetching charge count:', err);
    }
  };

  const handleObservationSubmit = async () => {
    if (!selectedObservation || !observationValue) {
      toast.error('Please select an observation and enter a value');
      return;
    }
    const selectedOption = observationOptions.find(o => o.label === selectedObservation);
    const validToken = await getValidToken();
    if (!validToken) { toast.error('Could not get valid authentication token'); return; }
    const rangeValidation = validateObservationRange(selectedOption.label, parseFloat(observationValue));
    if (!rangeValidation.isValid) toast.warning(rangeValidation.message);
    try {
      toast.info('Submitting observation...');
      const localDate = new Date(observationDate);
      localDate.setHours(12, 0, 0, 0);
      const observationResource = createObservationResource({
        userId: authUser.uid, observationType: selectedOption.label,
        value: observationValue, unit: selectedOption.unit, unitCode: selectedOption.code,
        loincCode: selectedOption.loinc, displayName: selectedOption.display,
        effectiveDateTime: localDate.toISOString(), status: 'final',
      });
      const validation = validateObservation(observationResource);
      if (!validation.isValid) {
        toast.error(`Validation failed: ${validation.errors.map(e => e.message).join('; ')}`);
        return;
      }
      const response = await fetch(`${config.fhirUrl}/fhir/Observation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/fhir+json', Authorization: `Bearer ${validToken}` },
        body: JSON.stringify(observationResource),
      });
      if (response.ok) {
        toast.success('Observation submitted successfully!');
        setSelectedObservation('');
        setObservationValue('');
        setObservationDate(new Date().toISOString().split('T')[0]);
        fetchFhirData();
      } else {
        throw new Error('Failed to submit observation.');
      }
    } catch {
      toast.error('Error submitting observation. Please try again.');
    }
  };

  const handleLoadDemoData = async () => {
    if (!authUser?.uid) { toast.error('Please sign in first'); return; }
    const validToken = await getValidToken();
    if (!validToken) { toast.error('Could not get valid authentication token'); return; }
    setIsLoadingDemoData(true);
    const demoObservations = generateDemoObservations();
    setDemoDataProgress({ current: 0, total: demoObservations.length });
    let successCount = 0, errorCount = 0;
    for (let i = 0; i < demoObservations.length; i++) {
      const obs = demoObservations[i];
      try {
        const resource = {
          resourceType: 'Observation', status: 'final',
          identifier: [{ system: 'https://fhir.mieros.cloud/fhir/ids', value: authUser.uid }],
          category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'vital-signs', display: 'Vital Signs' }] }],
          code: obs.option.loinc
            ? { coding: [{ system: 'http://loinc.org', code: obs.option.loinc, display: obs.option.display }], text: obs.option.label }
            : { text: obs.option.label },
          effectiveDateTime: obs.date,
          valueQuantity: { value: parseFloat(obs.value), unit: obs.option.unit, system: 'http://unitsofmeasure.org', code: obs.option.code },
        };
        const res = await fetch(`${config.fhirUrl}/fhir/Observation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/fhir+json', Authorization: `Bearer ${validToken}` },
          body: JSON.stringify(resource),
        });
        res.ok ? successCount++ : errorCount++;
      } catch { errorCount++; }
      setDemoDataProgress({ current: i + 1, total: demoObservations.length });
    }
    setIsLoadingDemoData(false);
    errorCount === 0
      ? toast.success(`Successfully loaded ${successCount} demo observations!`)
      : toast.warning(`Loaded ${successCount} observations, ${errorCount} failed`);
    fetchFhirData();
  };

  const handleLoadDemoCharges = async () => {
    if (!authUser?.uid) { toast.error('Please sign in first'); return; }
    const validToken = await getValidToken();
    if (!validToken) { toast.error('Could not get valid authentication token'); return; }
    setIsLoadingDemoCharges(true);
    const demoCharges = generateDemoCharges();
    setDemoChargeProgress({ current: 0, total: demoCharges.length });
    let successCount = 0, errorCount = 0;
    for (let i = 0; i < demoCharges.length; i++) {
      const charge = demoCharges[i];
      try {
        const chargeResource = {
          resourceType: 'ChargeItem', status: 'billable',
          identifier: [
            { system: 'https://fhir.mieros.cloud/fhir/ids', value: authUser.uid },
            { system: 'https://fhir.mieros.cloud/fhir/expense-ids', value: `EXP-${Date.now()}-${i}` },
          ],
          code: { coding: [{ system: 'https://fhir.mieros.cloud/expense-categories', code: charge.expense.code, display: charge.expense.display }], text: charge.expense.display },
          subject: { identifier: { system: 'https://fhir.mieros.cloud/fhir/ids', value: authUser.uid }, display: authUser.email || 'User' },
          occurrenceDateTime: charge.date,
          quantity: { value: 1 },
          priceOverride: { value: charge.amount, currency: 'USD' },
          extension: [{ url: 'https://fhir.mieros.cloud/expense-category', valueString: charge.expense.category }],
          enteredDate: new Date().toISOString(),
        };
        const res = await fetch(`${config.fhirUrl}/fhir/ChargeItem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/fhir+json', Authorization: `Bearer ${validToken}` },
          body: JSON.stringify(chargeResource),
        });
        res.ok ? successCount++ : errorCount++;
      } catch { errorCount++; }
      setDemoChargeProgress({ current: i + 1, total: demoCharges.length });
    }
    setIsLoadingDemoCharges(false);
    errorCount === 0
      ? toast.success(`Successfully loaded ${successCount} demo expenses!`)
      : toast.warning(`Loaded ${successCount} expenses, ${errorCount} failed`);
    fetchChargeCount();
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (authUser?.uid) {
      fetchFhirData();
      fetchChargeCount();
    }
  }, [authUser?.uid]);

  return (
    <div className="health-dashboard">
      <h1 className="dashboard-title">Health Overview</h1>
      {/* Nutrition Tracker */}
      <CRow className="mb-4">
        <CCol xs={12}>
          <NutritionTracker />
        </CCol>
      </CRow>
      {/* Observations summary */}
      <CRow className="mb-4">
        <CCol xs={12}>
          <CCard>
            <CCardHeader>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <h5 className="mb-0">Health Observations</h5>
                <div className="d-flex align-items-center gap-3">
                  <span className="health-copy-muted">Total: <strong>{observationCount}</strong></span>
                  <CButton color="primary" size="sm" variant="outline" onClick={fetchFhirData}>Refresh</CButton>
                </div>
              </div>
            </CCardHeader>
          </CCard>
        </CCol>
      </CRow>

      {/* Test data section */}
      <CRow className="mb-4">
        <CCol xs={12}>
          <CCard>
            <CCardHeader><h5 className="mb-0">Test Data</h5></CCardHeader>
            <CCardBody>
              {/* Demo Observations */}
              <div className="mb-4 p-3 rounded border border-info">
                <h6 className="health-copy-muted mb-2">🧪 Load Demo Observations</h6>
                <p className="health-copy-muted mb-2" style={{ fontSize: '0.9rem' }}>
                  Creates 7 days of sample health observations including Heart Rate, Body Temperature,
                  Oxygen Saturation, Blood Glucose, Steps, Sleep, Weight, Calories, Water Intake, and Blood Pressure.
                </p>
                <CButton color="info" onClick={handleLoadDemoData} disabled={isLoadingDemoData}>
                  {isLoadingDemoData ? `Loading... (${demoDataProgress.current}/${demoDataProgress.total})` : 'Load Demo Observations'}
                </CButton>
                {isLoadingDemoData && (
                  <div className="mt-2" style={{ width: '100%', height: 8, backgroundColor: 'var(--border-default)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${(demoDataProgress.current / demoDataProgress.total) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--electric-blue), var(--electric-purple))', transition: 'width 0.2s' }} />
                  </div>
                )}
              </div>

              {/* Demo Expenses */}
              <div className="mb-4 p-3 rounded border border-warning">
                <h6 className="health-copy-muted mb-2">💳 Load Demo Expenses</h6>
                <p className="health-copy-muted mb-1" style={{ fontSize: '0.9rem' }}>
                  Creates 7 days of realistic everyday expenses (~40+ transactions).
                </p>
                <p className="mb-2" style={{ fontSize: '0.85rem' }}>
                  Current Expense Count: <strong>{chargeCount}</strong>{' '}
                  <CButton color="link" size="sm" onClick={fetchChargeCount} className="p-0">🔄</CButton>
                </p>
                <CButton color="warning" onClick={handleLoadDemoCharges} disabled={isLoadingDemoCharges}>
                  {isLoadingDemoCharges ? `Loading... (${demoChargeProgress.current}/${demoChargeProgress.total})` : 'Load Demo Expenses'}
                </CButton>
                {isLoadingDemoCharges && (
                  <div className="mt-2" style={{ width: '100%', height: 8, backgroundColor: 'var(--border-default)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${(demoChargeProgress.current / demoChargeProgress.total) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--warning), var(--electric-pink))', transition: 'width 0.2s' }} />
                  </div>
                )}
              </div>

              {/* Manual entry */}
              <h6 className="mb-2">📝 Manual Observation Entry</h6>
              <select
                className="form-select mb-2"
                value={selectedObservation}
                onChange={(e) => setSelectedObservation(e.target.value)}
              >
                <option value="">Select an observation</option>
                {observationOptions.map((option, index) => (
                  <option key={index} value={option.label}>
                    {option.label} {option.loinc ? `(LOINC: ${option.loinc})` : ''}
                  </option>
                ))}
              </select>
              {selectedUnit && <span className="text-primary d-block mb-2">Unit: {selectedUnit}</span>}
              <CFormInput type="number" placeholder="Enter value" value={observationValue} onChange={(e) => setObservationValue(e.target.value)} className="mb-2" />
              <CFormInput type="date" value={observationDate} onChange={(e) => setObservationDate(e.target.value)} className="mb-2" />
              <CButton color="primary" size="sm" onClick={handleObservationSubmit}>Submit Observation</CButton>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* FHIR Observation Trends Chart */}
      <CRow className="mb-4">
        <CCol xs={12}>
          <ObservationTrendsChart />
        </CCol>
      </CRow>

      {/* FHIR Observations Table */}
      <CRow className="mb-4">
        <CCol xs={12}>
          <ObservationsTable />
        </CCol>
      </CRow>



      <CRow>
        <CCol xs={isMobile ? '12' : '6'}>
          <CCard className="dashboard-card">
            <div className="card-header">
              <img src={caloriesIcon} alt="Calories" className="card-icon" />
              <h2>Calories Burned</h2>
              <button className="export-button">Export Data to MiE</button>
            </div>
            <div className="chart-container">
              <GoogleFitCaloriesChart />
            </div>
          </CCard>
        </CCol>
        <CCol xs={isMobile ? '12' : '6'}>
          <CCard className="dashboard-card">
            <div className="card-header">
              <img src={bloodPressureIcon} alt="Blood Pressure" className="card-icon" />
              <h2>Blood Pressure</h2>
              <button className="export-button">Export Data to FHIR</button>
            </div>
            <div className="chart-container">
              <GoogleFitBloodPressureChart />
            </div>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        <CCol xs={isMobile ? '12' : '6'}>
          <CCard className="dashboard-card">
            <div className="card-header">
              <img src={weightIcon} alt="Weight" className="card-icon" />
              <h2>Weight</h2>
              <button className="export-button">Export Data to FHIR</button>
            </div>
            <div className="chart-container">
              <GoogleFitWeightChart />
            </div>
          </CCard>
        </CCol>
        <CCol xs={isMobile ? '12' : '6'}>
          <CCard className="dashboard-card">
            <div className="card-header">
              <img src={stepsIcon} alt="Steps" className="card-icon" />
              <h2>Steps Taken</h2>
              <button className="export-button">Export Data to FHIR</button>
            </div>
            <div className="chart-container">
              <GoogleFitStepsChart />
            </div>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        <CCol xs={isMobile ? '12' : '6'}>
          <CCard className="dashboard-card">
            <div className="card-header">
              <img src={sleepIcon} alt="Sleep" className="card-icon" />
              <h2>Sleep Hours</h2>
              <button className="export-button">Export Data to FHIR</button>
            </div>
            <GoogleFitSleepChart />
          </CCard>
        </CCol>
        <CCol xs={isMobile ? '12' : '6'}>
          <CCard className="dashboard-card">
            <div className="card-header">
              <img src={bloodSugarIcon} alt="Blood Sugar" className="card-icon" />
              <h2>Blood Sugar Levels</h2>
              <button className="export-button">Export Data to FHIR</button>
            </div>
            <div className="chart-wrapper" style={{ height: '300px', padding: '20px' }}>
              <GoogleFitBloodSugarChart />
            </div>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
};

export default HealthDashboard;
